"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Link2, CreditCard } from "lucide-react";
import Image from "@/components/Image";
import NormalButton from "../Button/normal";
import FreeVsProTable from "@/components/Subscription/FreeVsProTable";
import useAuthStore from "@/store/useAuthStore";
import useSubscriptionStore from "@/store/useSubscriptionStore";
import useHasParentAccount from '@/hooks/useHasParentAccount';
import { FEATURE_LABELS, FEATURE_QUOTAS, TIER_LABELS, type GatedFeature, type SubscriptionTier } from "@/constant/subscription";
import { isParentManagedSubscription, isStudentManagedSubscription } from "@/lib/subscription-management";

interface MembershipModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenLinkParent: () => void;
  onOpenSwitchParent: () => void;
  onOpenSubjectManager: () => void;
  onUpgradeSelf?: () => void;
  /** @deprecated Use `tier` instead */
  isPro?: boolean;
  /** Active subscription tier, or 'free' / undefined for free users */
  tier?: SubscriptionTier | null;
  isTrial?: boolean;
  trialEndDate?: string | null;
  gatedFeature?: GatedFeature;
  /** Override for dev/preview — skip the DB query for parent account detection */
  overrideHasParent?: boolean;
  overrideParentEmail?: string;
}

const MembershipModal = ({
  isOpen,
  onClose,
  onOpenLinkParent,
  onOpenSwitchParent,
  onOpenSubjectManager,
  onUpgradeSelf,
  isPro: isPropLegacy,
  tier: tierProp,
  isTrial,
  trialEndDate,
  gatedFeature,
  overrideHasParent,
  overrideParentEmail,
}: MembershipModalProps) => {
  // Resolve tier from new prop or legacy isPro
  const tier: SubscriptionTier = tierProp ?? (isPropLegacy ? 'plus' : 'free');
  const isPro = tier !== 'free';
  const [mounted, setMounted] = useState(false);
  const profile = useAuthStore((s) => s.profile);
  const subscription = useSubscriptionStore((s) => s.subscription);
  const isStudentFree = !isPro;
  const { hasParent: hasParentFromDB, parentEmail: parentEmailFromDB } = useHasParentAccount(isOpen && isStudentFree && overrideHasParent === undefined);
  const hasParent = overrideHasParent ?? hasParentFromDB;
  const parentEmail = overrideParentEmail ?? parentEmailFromDB;
  const activePaidSubscription =
    subscription && ['active', 'past_due'].includes(subscription.status) && !subscription.is_trial
      ? subscription
      : null;
  const isPaidManagedByParent = isPro && isParentManagedSubscription(activePaidSubscription);
  const isPaidManagedByStudent = isPro && isStudentManagedSubscription(activePaidSubscription);
  const normalizedSubject = String(profile?.free_subject || '').replace(/_/g, '-');
  const currentSubjectLabel =
    normalizedSubject === 'math' ? 'Math'
    : normalizedSubject === 'science' ? 'Science'
    : normalizedSubject === 'history' ? 'Social Studies'
    : normalizedSubject === 'vocabulary' ? 'Vocabulary'
    : normalizedSubject === 'grammar' ? 'Grammar'
    : normalizedSubject === 'amc-math' ? 'AMC Math'
    : '';
  const featureLabel = gatedFeature ? FEATURE_LABELS[gatedFeature] : null;
  const freeQuota = gatedFeature ? FEATURE_QUOTAS[gatedFeature].free : null;
  const showQuotaContext = Boolean(gatedFeature && featureLabel && Number.isFinite(freeQuota));

  const trialDaysLeft = isTrial && trialEndDate
    ? Math.max(0, Math.ceil((new Date(trialEndDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  if (!isOpen || !mounted) return null;

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 p-3 sm:p-4" style={{ zIndex: 1000 }}>
      <div className="relative mx-1 max-h-[92vh] overflow-hidden rounded-[24px] bg-white shadow-[0_30px_80px_rgba(17,24,39,0.18)] animate-fade-in w-[min(96vw,740px)] sm:w-[min(94vw,740px)]">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-20 flex h-8 w-8 items-center justify-center rounded-full text-[#667085] transition-colors hover:bg-[#F3F4F6] hover:text-[#344054] sm:right-5 sm:top-5 sm:h-9 sm:w-9"
          aria-label="Close membership dialog"
        >
          <X className="h-4 w-4 sm:h-5 sm:w-5" />
        </button>

        <div className="absolute inset-x-0 top-0 h-20 bg-[linear-gradient(135deg,rgba(255,220,188,0.55)_0%,rgba(243,244,245,0.2)_70%)]" />

        <div className="relative max-h-[92vh] overflow-y-auto px-4 pb-5 pt-4 sm:px-5 sm:pb-6 md:px-6 md:pb-7">
          <div className="flex flex-col items-center gap-2">
            <Image className="-mt-[4px] sm:-mt-[8px]" src="/assets/landing-logo.png" alt="penguin" width={46} height={46} />
            <div className="pt-[12px] text-center sm:pt-[20px]">
              <div className="display-text-semi text-[16px] leading-tight text-[#191C1D] sm:text-[17px] md:text-[19px]">
                {isPro
                  ? isTrial
                    ? 'Free Trial is Active'
                    : `You are a ${TIER_LABELS[tier]} member`
                  : showQuotaContext
                    ? `${featureLabel} limit reached`
                    : 'You are on the Free plan'}
              </div>
              <p className="display-text mt-1.5 max-w-[360px] text-[13px] leading-5 text-[#6B7280] sm:text-sm sm:leading-6">
                {isPro
                  ? isTrial && trialDaysLeft !== null
                    ? `Enjoy Plus features free for ${trialDaysLeft} more day${trialDaysLeft === 1 ? '' : 's'}!`
                    : tier === 'max'
                      ? 'Enjoy all features without limits.'
                      : 'Upgrade to Max to unlock unlimited access.'
                  : showQuotaContext
                    ? `You've used all ${freeQuota} free ${featureLabel?.toLowerCase()} sessions for this week. Compare plans below to keep going.`
                    : 'Unlock the full potential of your learning journey'}
              </p>
            </div>
          </div>

          <>
              <div className="mt-3.5 w-full overflow-x-auto">
                <div className="min-w-[580px]">
                  <FreeVsProTable
                    currentTier={tier}
                    isTrial={isTrial}
                    trialDaysLeft={trialDaysLeft}
                    subjectEntry={
                      isStudentFree
                        ? {
                            label: currentSubjectLabel ? `Current: ${currentSubjectLabel}` : 'Choose subject',
                            onClick: onOpenSubjectManager,
                          }
                        : undefined
                    }
                  />
                </div>
              </div>

              {tier === 'max' && (
                <div className="mt-5 flex justify-center">
                  <NormalButton onClick={onClose} colorType="green" className="min-w-[180px] rounded-full bg-[#43CF2C]">
                    Close
                  </NormalButton>
                </div>
              )}
              {tier !== 'max' && (
                <>
                  <div className="mt-4 flex items-start justify-center gap-2 rounded-[18px] border border-[#E8E1D5] bg-[#F3F4F5] px-3.5 py-3 sm:items-center sm:gap-2.5 sm:px-4">
                    <Link2 className="h-4 w-4 shrink-0 text-[#895100]" />
                    <p className="display-text text-center text-[13px] leading-5 text-[#6B7280] sm:text-sm sm:leading-6">
                      {isPaidManagedByParent
                        ? 'This plan is managed by your parent account. Open the parent account to change billing or upgrade the plan.'
                        : isPaidManagedByStudent
                          ? 'This plan is managed by your student account. You can keep using your own billing flow for upgrades.'
                        : hasParent
                        ? <>Your parent account is already connected{parentEmail ? <> (<span className="display-text-semi text-[#895100]">{parentEmail}</span>)</> : null}. We&apos;ll send them a secure link to continue this purchase.</>
                        : 'Send your parent a secure link so they can continue this purchase and manage billing for you.'}
                    </p>
                  </div>
                  <div className="mt-5 flex flex-col items-center gap-3">
                    {isPaidManagedByParent ? (
                      <NormalButton
                        onClick={onOpenSwitchParent}
                        colorType="green"
                        className="min-w-[280px]"
                      >
                        <span className="flex items-center justify-center gap-2 text-base">
                          <Link2 className="h-4 w-4" />
                          Open Parent Account
                        </span>
                      </NormalButton>
                    ) : isPaidManagedByStudent ? (
                      <>
                        {onUpgradeSelf ? (
                          <NormalButton
                            onClick={onUpgradeSelf}
                            colorType="yellow"
                            className="min-w-[280px]"
                          >
                            <span className="flex items-center justify-center gap-2 text-base">
                              <CreditCard className="h-4 w-4" />
                              Upgrade yourself
                            </span>
                          </NormalButton>
                        ) : null}
                        <NormalButton onClick={onClose} colorType="white" className="min-w-[280px]">
                          Close
                        </NormalButton>
                      </>
                    ) : (
                      <>
                        <NormalButton
                          onClick={onOpenLinkParent}
                          colorType="green"
                          className="min-w-[280px]"
                        >
                          <span className="flex items-center justify-center gap-2 text-base">
                            <Link2 className="h-4 w-4" />
                            {hasParent ? 'Continue with Parent' : 'Ask Parent to Continue'}
                          </span>
                        </NormalButton>
                        {onUpgradeSelf ? (
                          <NormalButton
                            onClick={onUpgradeSelf}
                            colorType="yellow"
                            className="min-w-[280px]"
                          >
                            <span className="flex items-center justify-center gap-2 text-base">
                              <CreditCard className="h-4 w-4" />
                              Upgrade yourself
                            </span>
                          </NormalButton>
                        ) : null}
                      </>
                    )}
                  </div>
                </>
              )}
            </>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default MembershipModal;
